/**
 * OrderingPacketsLane — packet-centric view inside OrderingQueue.
 *
 * Shows OrderingPackets as cards grouped by status. Clicking a card opens
 * the OrderPlacementModal where the back-office rep does the actual work.
 *
 * Filters: vendor, status, search.
 */
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaginationBar } from "@/components/PaginationBar";
import { Search, Package, AlertTriangle, Truck, Calendar, DollarSign, ArrowRight } from "lucide-react";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import {
  PACKET_STATUS_LABEL, PACKET_STATUS_TONE,
  type OrderingPacketStatus, type OrderingPacket,
} from "@/data/orderingPacket/types";
import { useOrderingPacketsPage } from "@/hooks/useOrderingQueueServerPaging";
import { ORDERING_QUEUE_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { OrderPlacementModal } from "@/components/ordering/OrderPlacementModal";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const TONE_BADGE: Record<string, string> = {
  muted: "bg-muted text-muted-foreground border-border",
  info: "bg-info/15 text-info border-info/30",
  warning: "bg-warning/15 text-warning border-warning/30",
  success: "bg-success/15 text-success border-success/30",
  destructive: "bg-destructive/15 text-destructive border-destructive/30",
};

const ACTIVE_STATUSES: OrderingPacketStatus[] = [
  "draft", "needs_review", "ready_to_place", "partially_placed",
  "placed", "backordered", "vendor_issue", "price_override_pending_review",
];

interface OrderingPacketsLaneProps {
  serverPagingEnabled?: boolean;
  refreshToken?: number;
  serverVendors?: string[];
}

export function OrderingPacketsLane({
  serverPagingEnabled = ORDERING_QUEUE_SERVER_PAGING_ENABLED,
  refreshToken = 0,
  serverVendors = [],
}: OrderingPacketsLaneProps) {
  const { packets, vendorLeadTimes } = useOrderingPacket();
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<OrderingPacketStatus | "all" | "active" | "closed">("active");
  const [openPacketId, setOpenPacketId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const serverPage = useOrderingPacketsPage({
    enabled: serverPagingEnabled,
    status: statusFilter,
    vendor: vendorFilter,
    search,
    page,
    pageSize,
    refreshToken,
  });

  useEffect(() => {
    setPage(0);
  }, [search, vendorFilter, statusFilter, pageSize]);

  const vendors = useMemo(() => {
    const set = new Set<string>();
    serverVendors.forEach((vendor) => set.add(vendor));
    packets.forEach((p) => set.add(p.intendedVendor));
    vendorLeadTimes.forEach((v) => set.add(v.vendorName));
    return Array.from(set).sort();
  }, [packets, serverVendors, vendorLeadTimes]);

  const filtered = useMemo(() => {
    if (serverPagingEnabled) return serverPage.packets;
    let list = packets;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((p) =>
        p.customerName.toLowerCase().includes(s) ||
        p.jobLabel.toLowerCase().includes(s) ||
        p.intendedVendor.toLowerCase().includes(s) ||
        p.id.toLowerCase().includes(s),
      );
    }
    if (vendorFilter !== "all") list = list.filter((p) => p.intendedVendor === vendorFilter);
    if (statusFilter === "active") list = list.filter((p) => ACTIVE_STATUSES.includes(p.status));
    else if (statusFilter === "closed") list = list.filter((p) => p.status === "received" || p.status === "cancelled");
    else if (statusFilter !== "all") list = list.filter((p) => p.status === statusFilter);
    return [...list].sort((a, b) => b.sentAt.localeCompare(a.sentAt));
  }, [packets, search, serverPage.packets, serverPagingEnabled, statusFilter, vendorFilter]);

  const serverTotalPages = Math.max(1, Math.ceil(serverPage.count / pageSize));
  const serverSafePage = Math.min(page, serverTotalPages - 1);
  const serverPagination = {
    page: serverSafePage,
    totalPages: serverTotalPages,
    totalItems: serverPage.count,
    pageStart: serverPage.count === 0 ? 0 : serverSafePage * pageSize + 1,
    pageEnd: Math.min((serverSafePage + 1) * pageSize, serverPage.count),
    hasNext: serverSafePage < serverTotalPages - 1,
    hasPrev: serverSafePage > 0,
    next: () => setPage((current) => Math.min(current + 1, serverTotalPages - 1)),
    prev: () => setPage((current) => Math.max(current - 1, 0)),
    pageSize,
    setPageSize,
  };

  // Group by status for the lane view
  const grouped = useMemo(() => {
    const map = new Map<OrderingPacketStatus, OrderingPacket[]>();
    filtered.forEach((p) => {
      const arr = map.get(p.status) ?? [];
      arr.push(p);
      map.set(p.status, arr);
    });
    return map;
  }, [filtered]);

  const statusOrder: OrderingPacketStatus[] = [
    "needs_review", "draft", "ready_to_place", "price_override_pending_review",
    "partially_placed", "placed", "backordered", "vendor_issue", "received", "cancelled",
  ];

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search packets…"
            className="pl-8 h-8 text-[12px]"
          />
        </div>
        <Select value={vendorFilter} onValueChange={setVendorFilter}>
          <SelectTrigger className="w-[160px] h-8 text-[11px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-[180px] h-8 text-[11px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="closed">Closed (received/cancelled)</SelectItem>
            {Object.keys(PACKET_STATUS_LABEL).map((k) => (
              <SelectItem key={k} value={k}>{PACKET_STATUS_LABEL[k as OrderingPacketStatus]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-muted-foreground ml-auto">
          {serverPagingEnabled
            ? `${serverPage.count} packet${serverPage.count === 1 ? "" : "s"}`
            : `${filtered.length} packet${filtered.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {serverPagingEnabled && serverPage.loading && (
        <Card className="p-4 text-[12px] text-muted-foreground">
          Loading ordering packets...
        </Card>
      )}

      {/* Empty state */}
      {!serverPage.loading && filtered.length === 0 && (
        <Card className="p-8 text-center">
          <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-[13px] font-semibold">No ordering packets</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Packets appear here when "Send to Ordering" is clicked from a job.
          </p>
        </Card>
      )}

      {/* Grouped by status */}
      <div className="space-y-5">
        {statusOrder.map((status) => {
          const items = grouped.get(status);
          if (!items || items.length === 0) return null;
          const tone = PACKET_STATUS_TONE[status];
          return (
            <div key={status}>
              <div className="flex items-center gap-2 mb-2">
                <Badge className={cn("text-[10px] font-semibold border", TONE_BADGE[tone])}>
                  {PACKET_STATUS_LABEL[status]}
                </Badge>
                <span className="text-[11px] text-muted-foreground tabular-nums">{items.length}</span>
              </div>
              <div className="grid gap-2 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                {items.map((packet) => (
                  <PacketCard
                    key={packet.id}
                    packet={packet}
                    readOnly={Boolean((packet as { legacyReadOnly?: boolean }).legacyReadOnly)}
                    onOpen={() => setOpenPacketId(packet.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {serverPagingEnabled && <PaginationBar {...serverPagination} />}

      <OrderPlacementModal
        open={!!openPacketId}
        onOpenChange={(o) => !o && setOpenPacketId(null)}
        packetId={openPacketId}
      />
    </div>
  );
}

function PacketCard({
  packet,
  readOnly = false,
  onOpen,
}: {
  packet: OrderingPacket;
  readOnly?: boolean;
  onOpen: () => void;
}) {
  const variance = packet.actualTotal - packet.projectedTotal;
  return (
    <Card
      className={cn(
        "p-3 transition-colors",
        readOnly
          ? "border-dashed bg-muted/10"
          : "cursor-pointer hover:border-primary/40 hover:bg-muted/20",
      )}
      onClick={readOnly ? undefined : onOpen}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-bold truncate">{packet.customerName}</p>
          <p className="text-[10px] text-muted-foreground truncate">{packet.jobLabel}</p>
        </div>
        {readOnly ? (
          <Badge variant="outline" className="text-[9px] border-border text-muted-foreground shrink-0">
            Legacy PO
          </Badge>
        ) : packet.exceptionCount > 0 && (
          <Badge variant="outline" className="text-[9px] border-warning/40 text-warning gap-1 shrink-0">
            <AlertTriangle className="h-2.5 w-2.5" /> {packet.exceptionCount}
          </Badge>
        )}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
        <div className="flex items-center gap-1 text-muted-foreground truncate">
          <Truck className="h-3 w-3 shrink-0" /> {packet.intendedVendor}
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <Package className="h-3 w-3" /> {packet.lineItems.length} item{packet.lineItems.length === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <DollarSign className="h-3 w-3" />
          <span className="tabular-nums">${packet.projectedTotal.toLocaleString()}</span>
          {packet.actualTotal > 0 && (
            <span className={cn("tabular-nums ml-1", variance > 0 ? "text-warning" : variance < 0 ? "text-success" : "")}>
              ({variance > 0 ? "+" : ""}${Math.abs(variance).toLocaleString()})
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <Calendar className="h-3 w-3" />
          {packet.expectedDeliveryDate ? format(new Date(packet.expectedDeliveryDate), "MMM d") : "ETA TBD"}
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
        <span className="text-[9px] text-muted-foreground">
          Sent {format(new Date(packet.sentAt), "MMM d, h:mm a")}
        </span>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] gap-1" disabled={readOnly}>
          {readOnly ? "Read-only" : "Open"} {!readOnly && <ArrowRight className="h-2.5 w-2.5" />}
        </Button>
      </div>
    </Card>
  );
}
